import React, { useEffect, useRef, useState } from 'react';

import {
  barcodes,
  checkbox,
  dateTime,
  ellipse,
  image,
  line,
  radioGroup,
  rectangle,
  select,
  table,
  text,
  time,
} from '@pdfme/schemas';
import { Viewer } from '@pdfme/ui';
import {
  AvatarPhotoEditor,
  Box,
  Button,
  CanAccess,
  Loader,
  LoadingButton,
  PageHeader,
  SignaturePad,
  Tab,
  Tabs,
} from 'components';
import { useNotifications } from 'hooks';

import {
  Designer,
  Template,
  getInputFromTemplate,
  useNavigate,
  useSelector,
  useTheme,
  useTranslation,
} from 'third-party';

import { bankCheckListUrl } from 'constants/url';

import { useBank } from 'modules/bank';
import { useSettings } from 'modules/setting';

import { appSelectors, bankSelectors, settingSelectors } from 'selectors';

import {
  getSampleCheckTemplate,
  previewSampleData,
} from 'features/CheckDesigner/sampleData';

import { getImageBase64FromForm } from 'utils/check';
import { removeNullFields } from 'utils/helper';

import { APIBank, PrintType } from 'types/api';
import { PlanFeature } from 'types/app';

const plugins = {
  text,
  image,
  qrcode: barcodes.qrcode,
  line,
  table,
  rectangle,
  ellipse,
  dateTime,
  time,
  select,
  checkbox,
  radioGroup,
};

const defaultTemplate = getSampleCheckTemplate();

const buildDisplayData = (bank: APIBank | null): Record<string, string> => {
  if (!bank) return previewSampleData as Record<string, string>;
  const address = `${bank.address}\n${bank.city}, ${bank.state} ${bank.zip}`;
  const bankInfo = `\u2446${bank.routing}\u2446  ${bank.account}\u2446  001001`;
  return {
    ...(previewSampleData as Record<string, string>),
    bankName: bank.bankName,
    companyName: bank.companyName,
    companyAddress: address,
    compnayAddressMiddle: address,
    bankInfo,
  };
};

const buildViewerInputs = (tmpl: Template, bank: APIBank | null) => {
  const displayData = buildDisplayData(bank);
  return getInputFromTemplate(tmpl).map(input => {
    const result = { ...input };
    Object.keys(result).forEach(key => {
      if (displayData[key] !== undefined) {
        result[key] = displayData[key];
      }
    });
    return result;
  });
};
type CreateEditProps = {
  onModalClose: () => void;
  bankId?: string | null;
};

export const SimpleBankDesigner: React.FC<CreateEditProps> = ({
  onModalClose,
  bankId,
}) => {
  const loadCheckTemplatePending = useSelector(
    settingSelectors.loadCheckTemplatePending,
  );

  const [templates, setTemplates] = useState<Template[]>([]);

  const bank = useSelector(bankSelectors.bank);

  const { loadCheckTemplate } = useSettings();
  const { loadBank, clearBank } = useBank();

  useEffect(() => {
    if (bankId) {
      loadBank(bankId);
    }
    return () => {
      clearBank();
    };
  }, [bankId, loadBank, clearBank]);

  useEffect(() => {
    const loadTemplate = async (bankId: string, type: PrintType) => {
      const result = await loadCheckTemplate(bankId, type);

      if (result.payload == null) {
        setTemplates(it => [defaultTemplate, ...it]);
        return;
      }

      const cleanPayload = removeNullFields(result.payload);
      setTemplates(it => [cleanPayload, ...it]);
    };

    if (!bankId) {
      return;
    }

    loadTemplate(bankId, PrintType.Check).then(() =>
      loadTemplate(bankId, PrintType.WhitePaper),
    );
  }, [bankId, loadCheckTemplate]);

  if (loadCheckTemplatePending || templates.length < 1) {
    return <Loader />;
  }

  return (
    <CreateEditForm
      bankId={bankId}
      bank={bank}
      checkTemplates={templates}
      onModalClose={onModalClose}
    />
  );
};

type Props = {
  checkTemplates: Template[];
  bankId?: string | null;
  bank?: APIBank | null;
  onModalClose: () => void;
};

export const CreateEditForm: React.FC<Props> = ({
  checkTemplates,
  bankId,
  bank,
  onModalClose,
}) => {
  const avatarEditorRef =
    React.useRef<React.ElementRef<typeof AvatarPhotoEditor>>(null);

  const signaturePadRef =
    React.useRef<React.ElementRef<typeof SignaturePad>>(null);

  const { t } = useTranslation();
  const navigate = useNavigate();

  const saveCheckTemplatePending = useSelector(
    settingSelectors.saveCheckTemplatePending,
  );

  const organizationId = useSelector(appSelectors.organizationId);
  const designerRef = useRef<HTMLDivElement | null>(null);
  const designer = useRef<Designer | null>(null);
  const theme = useTheme();
  const { success } = useNotifications();

  const [templates, setTemplates] = useState<Template[]>(checkTemplates);
  const [prevDesignerRef, setPrevDesignerRef] = useState<Designer | null>(null);

  const { saveCheckTemplate } = useSettings();

  useEffect(() => {
    //@ts-ignore
    if (designerRef.current && designerRef != prevDesignerRef) {
      if (prevDesignerRef && designer.current) {
        designer.current.destroy();
      }

      // Moved buildDesigner function inside useEffect
      if (designerRef.current && templates) {
        //@ts-ignore
        designer.current = new Viewer({
          domContainer: designerRef.current,
          template: templates[0],
          inputs: buildViewerInputs(templates[0], bank ?? null),
          plugins: plugins,
          options: {
            lang: 'en',
            theme: {
              token: {
                colorPrimary: theme.custom.palette.primary300,
                colorPrimaryHover: theme.custom.palette.primary800,
              },
            },
          },
        });
      }

      //@ts-ignore
      setPrevDesignerRef(designerRef);
    }
  }, [bank, templates, prevDesignerRef, theme]);

  const withContent = (
    template: Template,
    filedName: 'logo' | 'signature',
    content: string | null,
  ): Template => {
    if (content === null) {
      return template;
    }

    return {
      ...template,
      schemas: [
        template.schemas[0].map(item =>
          item.name === filedName ? { ...item, content } : item,
        ),
      ],
      basePdf: template.basePdf || '',
    } as Template;
  };

  const readEditorContent = async (
    read: (() => Promise<FormData | null>) | undefined,
    fieldName: string,
  ) => {
    try {
      const form = await read?.();
      return form ? await getImageBase64FromForm(form, fieldName) : null;
    } catch {
      return null;
    }
  };

  const handleSave = async () => {
    if (!bankId) {
      return;
    }

    // The editors only push into `templates` via their own save icons, so pick up unapplied edits here.
    const logo = await readEditorContent(
      avatarEditorRef.current?.getAvatarImage,
      'file',
    );
    const signature = await readEditorContent(
      signaturePadRef.current?.getSignature,
      'signature',
    );
    const [whitePaper, check] = templates.map(template =>
      withContent(withContent(template, 'logo', logo), 'signature', signature),
    );

    await saveCheckTemplate(bankId, whitePaper, PrintType.WhitePaper);
    await saveCheckTemplate(bankId, check, PrintType.Check);

    success(t('simpleCheckDesigner.success'));
    onModalClose();
  };

  const handleCancel = () => {
    onModalClose();
  };

  const updateTemplate = (
    index: number,
    filedName: 'logo' | 'signature',
    content: string,
  ) => {
    const template = templates[index];
    if (!template) {
      return;
    }

    const obj = withContent(template, filedName, content);

    setTemplates(it => it.map((item, i) => (i === index ? obj : item)));
    setPrevDesignerRef(null);
    designer.current?.updateTemplate(obj);
  };

  const handleAvatarUpdate = async () => {
    const avatarFormData = await avatarEditorRef.current?.getAvatarImage();
    if (!avatarFormData) {
      return;
    }

    const logoContent = await getImageBase64FromForm(avatarFormData);

    updateTemplate(0, 'logo', logoContent);
    updateTemplate(1, 'logo', logoContent);
  };

  const handleAdvancedDesignerClick = () => {
    navigate(bankCheckListUrl(organizationId, bankId!));
  };

  const avatar = templates[0]?.schemas[0].find(item => item.name === 'logo');
  const signature = templates[0]?.schemas[0].find(
    item => item.name === 'signature',
  );

  const [value, setValue] = React.useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  function a11yProps(index: number) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  }

  const handleSignatureSave = async (form: FormData | null) => {
    if (!form) return;

    const logoContent = await getImageBase64FromForm(form, 'signature');

    updateTemplate(0, 'signature', logoContent);
    updateTemplate(1, 'signature', logoContent);
  };

  return (
    <Box padding="1rem  1rem  0 1rem">
      <PageHeader
        header={t('simpleCheckDesigner.header')}
        subHeader={t('simpleCheckDesigner.subHeader')}
      >
        <Box display="flex" columnGap="1rem">
          <LoadingButton
            type="submit"
            variant="outlined"
            disabled={saveCheckTemplatePending}
            loading={saveCheckTemplatePending}
            onClick={handleSave}
            size="medium"
            color="primary"
          >
            {t('common.form.save')}
          </LoadingButton>
          <CanAccess requiresFeature={PlanFeature.CheckDesigner}>
            <Button
              variant="outlined"
              disabled={saveCheckTemplatePending}
              onClick={handleAdvancedDesignerClick}
              size="medium"
              color="secondary"
            >
              {t('simpleCheckDesigners.advancedDesigner')}
            </Button>
          </CanAccess>
          <Button
            type="button"
            variant="outlined"
            onClick={handleCancel}
            size="medium"
            color="error"
          >
            {t('common.form.cancel')}
          </Button>
        </Box>
      </PageHeader>
      <Box display="flex" flexDirection="row" gap="2rem">
        <Box width="20rem">
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={value}
              onChange={handleChange}
              aria-label="basic tabs example"
            >
              <Tab label="Check Logo" {...a11yProps(0)} />
              <Tab label="Signature" {...a11yProps(1)} />
            </Tabs>
          </Box>
          <Box
            role="tabpanel"
            hidden={value !== 0}
            id="simple-tabpanel-0"
            aria-labelledby="simple-tab-0"
            sx={{ pt: 2 }}
          >
            <AvatarPhotoEditor
              ref={avatarEditorRef}
              //@ts-ignore
              isRound={false}
              avatarUrl={avatar?.content ?? ''}
              onAvatarSave={handleAvatarUpdate}
            />
          </Box>
          <Box
            role="tabpanel"
            hidden={value !== 1}
            id="simple-tabpanel-1"
            aria-labelledby="simple-tab-1"
            sx={{ pt: 2 }}
          >
            <Box width="19rem" height="10rem" border="1px solid black">
              <SignaturePad
                ref={signaturePadRef}
                defaultSignature={signature?.content ?? ''}
                onSave={handleSignatureSave}
              />
            </Box>
          </Box>
        </Box>
        <div
          ref={designerRef}
          style={{
            width: '25rem',
          }}
        />
      </Box>
    </Box>
  );
};
